/**
 * Interactive Site Map Drawer Plugin (mobile only)
 *
 * Mounts `[data-panel]` into the drawer starting in a collapsed (72 px peek) state.
 * Tap expands to full open; swipe-down snaps back to collapsed — both behaviours are
 * owned by `drawer.js` via the `peek` option passed to `mountElement`.
 *
 * Also subscribes to `selectDrawerMode` (derived from `activeView`) and calls
 * `drawerService.expandToFullSize()` / `collapseToPartial()` accordingly — see
 * DRAWER_PLUGIN_SEQUENCE.md. The plugin never decides its own size; it only reflects the selector.
 *
 * `restorePanel` is registered so the panel node is moved back to its original parent
 * before `drawerContainer.innerHTML` is cleared, preventing the node from being destroyed.
 *
 * When to touch this file:
 *  - Any new drawer lifecycle hooks (chip changes, view transitions, etc.).
 *  - If `drawerService` API changes (mountElement, onHtmlRemoved, clearHtml, expandToFullSize,
 *    collapseToPartial).
 *
 * Mobile-only is enforced by gating on `context.state.isMobile`
 */
import { distinctUntilChanged, map } from 'rxjs/operators';
import DrawerMode from 'layout/enums/drawerMode';
import { selectDrawerMode } from '../redux/selectors';

export const DRAWER_ID = 'native-interactive-site-map-drawer';
const PEEK_HEIGHT = '54px';

export default function createInteractiveSiteMapDrawerPlugin() {
  const state = {
    mounted: false,
    subscriptions: [],
  };

  return {
    name: DRAWER_ID,

    async start(context) {
      if (!context.state.isMobile) {
        return;
      }

      const { drawerService, store$ } = context.services;
      if (!drawerService) {
        return;
      }

      const panelElement = context.root.querySelector('[data-panel]');
      if (!panelElement) {
        return;
      }

      const originalParent = panelElement.parentElement;

      function restorePanel() {
        if (panelElement.parentElement !== originalParent) {
          originalParent.appendChild(panelElement);
        }
      }

      function onDrawerModeChanged(mode) {
        if (mode === DrawerMode.Full) {
          drawerService.expandToFullSize();
        } else {
          drawerService.collapseToPartial();
        }
      }

      // Register restorePanel BEFORE mountElement to guarantee it is in place
      // before the drawer can be closed. Race: clearHtml could be dispatched
      // between mountElement and onHtmlRemoved registration if reversed.
      drawerService.onHtmlRemoved({ name: DRAWER_ID, callback: restorePanel });
      drawerService.mountElement(panelElement, { peek: PEEK_HEIGHT });
      state.mounted = true;

      state.subscriptions.push(
        store$.pipe(
          map(selectDrawerMode),
          distinctUntilChanged(),
        ).subscribe(onDrawerModeChanged),
      );
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      if (!state.mounted) {
        return;
      }
      context.services.drawerService?.clearHtml?.();
      state.mounted = false;
    },
  };
}
